import type {
  PullRequestAction,
  PullRequestDetail,
  PullRequestMergeMethod,
  PullRequestUpdateMethod,
} from "@cmmd-forge/contracts";
import {
  ArrowUpRightIcon,
  GitMergeIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  GitPullRequestIcon,
  MoreHorizontalIcon,
  LinkIcon,
  RefreshCwIcon,
} from "lucide-react";

import { Button } from "~/components/ui/button";
import { useCopyToClipboard } from "~/hooks/useCopyToClipboard";
import type { PullRequestAgentSelection } from "~/pullRequest/pullRequestSelectionContext";
import { PullRequestHandoffMenuItems } from "../PullRequestHandoffActions";
import {
  Menu,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from "~/components/ui/menu";

export const MERGE_METHOD_LABELS: Record<PullRequestMergeMethod, string> = {
  merge: "Merge",
  squash: "Squash",
  rebase: "Rebase",
};

const UPDATE_METHOD_LABELS: Record<PullRequestUpdateMethod, string> = {
  merge: "Merge commit",
  rebase: "Rebase",
};

export interface PullRequestActionsMenuProps {
  readonly actions: ReadonlyArray<PullRequestAction>;
  readonly mergeMethod: PullRequestMergeMethod;
  readonly mergeMethods: ReadonlyArray<PullRequestMergeMethod>;
  readonly updateMethod: PullRequestUpdateMethod;
  readonly updateMethods: ReadonlyArray<PullRequestUpdateMethod>;
  readonly pending: boolean;
  readonly showMergeMethods: boolean;
  readonly showUpdateMethods: boolean;
  readonly onMergeMethodChange: (value: PullRequestMergeMethod) => void;
  readonly onUpdateMethodChange: (value: PullRequestUpdateMethod) => void;
  readonly onRun: (action: PullRequestAction) => void;
  readonly refreshing: boolean;
  readonly onRefresh: () => Promise<void>;
  readonly detail: Pick<PullRequestDetail, "mergeability" | "provider" | "state" | "url">;
  readonly selection: PullRequestAgentSelection | null;
}

export function PullRequestActionsMenu(props: PullRequestActionsMenuProps) {
  const copy = useCopyToClipboard();
  const showsMethodSelection = props.showMergeMethods || props.showUpdateMethods;
  return (
    <Menu>
      <MenuTrigger
        render={
          <Button aria-label="More pull request actions" size="icon-xs" variant="outline">
            <MoreHorizontalIcon aria-hidden className="size-3.5" />
          </Button>
        }
      />
      <MenuPopup align="end" className="min-w-52">
        <MenuItem disabled={props.refreshing} onClick={() => void props.onRefresh()}>
          <RefreshCwIcon
            aria-hidden
            className={props.refreshing ? "size-3.5 motion-safe:animate-spin" : "size-3.5"}
          />
          {props.refreshing ? "Refreshing…" : "Refresh"}
        </MenuItem>
        <PullRequestHandoffMenuItems detail={props.detail} selection={props.selection} />
        <MenuSeparator />
        <PullRequestActionMenuItems {...props} />
        {showsMethodSelection ? <MenuSeparator /> : null}
        <PullRequestMethodSelections {...props} />
        <MenuSeparator />
        <MenuItem onClick={() => window.open(props.detail.url, "_blank", "noopener,noreferrer")}>
          <ArrowUpRightIcon aria-hidden className="size-3.5" />
          Open on {providerLabel(props.detail.provider)}
        </MenuItem>
        <MenuItem onClick={() => copy.copyToClipboard(props.detail.url, undefined)}>
          <LinkIcon aria-hidden className="size-3.5" />
          {copy.isCopied ? "Copied" : "Copy link"}
        </MenuItem>
      </MenuPopup>
    </Menu>
  );
}

function PullRequestActionMenuItems(
  props: Pick<
    PullRequestActionsMenuProps,
    "actions" | "pending" | "onRun" | "refreshing" | "onRefresh"
  >,
) {
  return (
    <>
      {props.actions.map((action) => (
        <MenuItem key={action} disabled={props.pending} onClick={() => props.onRun(action)}>
          <PullRequestActionIcon action={action} />
          {props.pending ? "Working…" : pullRequestActionLabel(action)}
        </MenuItem>
      ))}
    </>
  );
}

function providerLabel(provider: string | undefined): string {
  if (provider === undefined) return "provider";
  return provider.toLowerCase() === "github"
    ? "GitHub"
    : provider.toLowerCase() === "gitlab"
      ? "GitLab"
      : provider;
}

function PullRequestMethodSelections(
  props: Pick<
    PullRequestActionsMenuProps,
    | "mergeMethod"
    | "mergeMethods"
    | "updateMethod"
    | "updateMethods"
    | "pending"
    | "showMergeMethods"
    | "showUpdateMethods"
    | "onMergeMethodChange"
    | "onUpdateMethodChange"
  >,
) {
  return (
    <>
      {props.showMergeMethods ? (
        <PullRequestMethodGroup
          label="Merge method"
          methods={props.mergeMethods}
          value={props.mergeMethod}
          labels={MERGE_METHOD_LABELS}
          pending={props.pending}
          onChange={props.onMergeMethodChange}
        />
      ) : null}
      {props.showMergeMethods && props.showUpdateMethods ? <MenuSeparator /> : null}
      {props.showUpdateMethods ? (
        <PullRequestMethodGroup
          label="Update method"
          methods={props.updateMethods}
          value={props.updateMethod}
          labels={UPDATE_METHOD_LABELS}
          pending={props.pending}
          onChange={props.onUpdateMethodChange}
        />
      ) : null}
    </>
  );
}

function PullRequestMethodGroup<Method extends string>(props: {
  readonly label: string;
  readonly methods: ReadonlyArray<Method>;
  readonly value: Method;
  readonly labels: Readonly<Record<Method, string>>;
  readonly pending: boolean;
  readonly onChange: (value: Method) => void;
}) {
  return (
    <MenuRadioGroup value={props.value} onValueChange={(value) => props.onChange(value as Method)}>
      <MenuGroupLabel>{props.label}</MenuGroupLabel>
      {props.methods.map((method) => (
        <MenuRadioItem key={method} value={method} disabled={props.pending}>
          <span className="flex min-w-0 items-center gap-2">
            <GitMergeIcon aria-hidden className="size-3.5" />
            <span>{props.labels[method]}</span>
          </span>
        </MenuRadioItem>
      ))}
    </MenuRadioGroup>
  );
}

function PullRequestActionIcon({ action }: { readonly action: PullRequestAction }) {
  if (action === "ready") return <GitPullRequestIcon aria-hidden className="size-3.5" />;
  if (action === "draft") return <GitPullRequestDraftIcon aria-hidden className="size-3.5" />;
  if (action === "close" || action === "reopen") {
    return <GitPullRequestClosedIcon aria-hidden className="size-3.5" />;
  }
  return <GitMergeIcon aria-hidden className="size-3.5" />;
}

export function pullRequestActionLabel(action: PullRequestAction): string {
  if (action === "ready") return "Ready for review";
  if (action === "draft") return "Convert to draft";
  if (action === "update-branch") return "Update branch";
  if (action === "enable-auto-merge") return "Enable auto-merge";
  if (action === "disable-auto-merge") return "Disable auto-merge";
  if (action === "close") return "Close pull request";
  if (action === "reopen") return "Reopen pull request";
  return "Merge";
}
